import { useEffect, useState } from 'react';
import {
    Grid, Typography, Tabs, Tab, TextField, Card, CardContent,
    CircularProgress, Box, Select, MenuItem, InputLabel, FormControl, Chip, OutlinedInput, Alert, Tooltip,
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { DatasetType } from '@mui/x-charts/models/seriesType/config';
import { getTemplateDashboard, getTemplateDashboardByProgram, getTemplateBlocks } from 'data/template-tracking';
import { ProgramsRequest } from 'api/ProgramsRequest';

type TemplateStats = DatasetType[number] & {
    template_name: string;
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    blocked: number;
    frequency_capped: number;
    quality_flagged: number;
    template_paused: number;
    template_disabled: number;
    undeliverable: number;
    delivery_rate: number;
    read_rate: number;
}

type ProgramStats = DatasetType[number] & {
    template_name: string;
    program_id: number;
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    blocked: number;
    frequency_capped: number;
    quality_flagged: number;
    template_paused: number;
    template_disabled: number;
    undeliverable: number;
    delivery_rate: number;
    read_rate: number;
}

interface BlockRecord {
    id: number;
    user_channel: string;
    template_name: string;
    program_id: number;
    wa_id: string;
    created_at: string;
}

function defaultTemplates(data: TemplateStats[]): string[] {
    if (!data.length) return [];
    const sorted = [...data].sort((a, b) => b.sent - a.sent);
    const top3 = sorted.slice(0, 3).map(r => r.template_name);
    const bottom3 = sorted.slice(-3).map(r => r.template_name);
    return Array.from(new Set([...top3, ...bottom3]));
}

function defaultPrograms(byProgram: ProgramStats[]): number[] {
    const totals = byProgram.reduce<Record<number, number>>((acc, r) => {
        acc[r.program_id] = (acc[r.program_id] ?? 0) + r.sent;
        return acc;
    }, {});
    return Object.entries(totals)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3)
        .map(([id]) => Number(id));
}

const MAX_CHIPS = 5;
const chipSx = { maxWidth: 'calc(50% - 4px)', overflow: 'hidden', '.MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' } };

function ChipsRenderValue({ values, getLabel }: { values: string[] | number[]; getLabel: (v: any) => string }) {
    const shown = (values as any[]).slice(0, MAX_CHIPS);
    const extra = values.length - MAX_CHIPS;
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%', overflow: 'hidden' }}>
            {shown.map((v: any) => <Chip key={v} label={getLabel(v)} size="small" sx={chipSx} />)}
            {extra > 0 && <Chip label={`+${extra}`} size="small" variant="outlined" sx={{ flexShrink: 0 }} />}
        </Box>
    );
}

function qualityRateColor(rate: number): string {
    if (rate >= 1) return '#F44336';
    if (rate >= 0.5) return '#FF9800';
    return '#4CAF50';
}

function freqRateColor(rate: number): string {
    if (rate >= 5) return '#F44336';
    if (rate >= 3) return '#FF9800';
    return '#4CAF50';
}

function Semaforo({ delivery_rate, read_rate, quality_rate, frequency_rate }: { delivery_rate: number; read_rate: number; quality_rate: number; frequency_rate: number }) {
    const deliveryColor = delivery_rate >= 85 ? '#1565C0' : '#F44336';
    const deliveryTip = delivery_rate >= 85
        ? 'OK — mensajes llegando bien al teléfono'
        : 'Problema — menos del 85% llegó. Revisar números inválidos o bloqueos de red.';

    const readColor = read_rate >= 40 ? '#E65100' : '#F44336';
    const readTip = read_rate >= 40
        ? 'OK — buena tasa de apertura'
        : 'Alerta — menos del 40% leyó el mensaje. Revisar contenido u horario de envío.';

    // Calidad: bloqueados + quality_flagged + undeliverable (131026, bucket ambiguo de Meta que puede
    // incluir bloqueos silenciosos sin que Meta especifique la causa). Señal directa de que el template molesta.
    const qualityTip = quality_rate < 0.5
        ? 'OK — sin señales de rechazo por calidad'
        : quality_rate < 1
        ? 'Monitorear — usuarios bloqueando, reportando, o mensajes no entregados sin causa clara. Meta puede degradar la entrega.'
        : 'Problema — revisar contenido y relevancia del template. Meta puede pausarlo.';

    // Freq. Cap: Meta bloqueó el envío porque el usuario ya recibió demasiados mensajes de otros negocios ese día.
    // No es culpa del template — es protección del ecosistema Meta. Preocuparse solo si supera el 5%.
    const freqTip = frequency_rate < 3
        ? 'OK — frecuencia normal. Meta limitó a algunos usuarios por saturación de otros negocios.'
        : frequency_rate < 5
        ? 'Monitorear — frecuencia alta. Considerar espaciar más los envíos al mismo segmento.'
        : 'Problema — demasiados usuarios saturados. Reducir frecuencia de campañas al mismo segmento.';

    return (
        <Box display="flex" gap={1} mt={1} flexWrap="wrap">
            <Tooltip title={deliveryTip} arrow placement="top">
                <Box sx={{ bgcolor: '#E3F2FD', borderRadius: 1, px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
                    <Typography variant="caption" color="textSecondary">Entrega</Typography>
                    <Typography variant="caption" fontWeight="bold" style={{ color: deliveryColor }}>{delivery_rate}%</Typography>
                </Box>
            </Tooltip>
            <Tooltip title={readTip} arrow placement="top">
                <Box sx={{ bgcolor: '#FFF8E1', borderRadius: 1, px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
                    <Typography variant="caption" color="textSecondary">Lectura</Typography>
                    <Typography variant="caption" fontWeight="bold" style={{ color: readColor }}>{read_rate}%</Typography>
                </Box>
            </Tooltip>
            {/* Calidad: bloqueados + quality_flagged — señal real de rechazo. Rojo >1% */}
            <Tooltip title={qualityTip} arrow placement="top">
                <Box sx={{ bgcolor: '#FAFAFA', border: `1px solid ${qualityRateColor(quality_rate)}`, borderRadius: 1, px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: qualityRateColor(quality_rate), flexShrink: 0 }} />
                    <Typography variant="caption" color="textSecondary">Calidad</Typography>
                    <Typography variant="caption" fontWeight="bold" style={{ color: qualityRateColor(quality_rate) }}>{quality_rate}%</Typography>
                </Box>
            </Tooltip>
            {/* Freq. Cap: límite de Meta por ecosistema — no es culpa del template. Rojo >5% */}
            <Tooltip title={freqTip} arrow placement="top">
                <Box sx={{ bgcolor: '#FAFAFA', border: `1px solid ${freqRateColor(frequency_rate)}`, borderRadius: 1, px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: freqRateColor(frequency_rate), flexShrink: 0 }} />
                    <Typography variant="caption" color="textSecondary">Freq. Cap</Typography>
                    <Typography variant="caption" fontWeight="bold" style={{ color: freqRateColor(frequency_rate) }}>{frequency_rate}%</Typography>
                </Box>
            </Tooltip>
        </Box>
    );
}

function ErrorCodeTooltip({ row, category }: { row: Record<string, any>; category: string }) {
    const codes = row?.push_metrics?.errors_by_type?.[category]?.codes as
        { code: number | null; title: string; count: number }[] | undefined;
    if (!codes || !codes.length) return <span>Sin detalle de códigos disponible</span>;
    return (
        <Box>
            {codes.map((c, i) => (
                <Typography key={i} variant="caption" display="block">
                    {c.count}× — ({c.code ?? 's/n'}) {c.title}
                </Typography>
            ))}
        </Box>
    );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
    return (
        <Card style={{ borderLeft: `4px solid ${color}` }}>
            <CardContent>
                <Typography variant="body2" color="textSecondary">{label}</Typography>
                <Typography variant="h5">{value}</Typography>
            </CardContent>
        </Card>
    );
}

const STATS_ROW = [
    { dataKey: 'sent', label: 'Enviados', color: '#2196F3' },
    { dataKey: 'delivered', label: 'Entregados', color: '#4CAF50' },
    { dataKey: 'read', label: 'Leídos', color: '#FF9800' },
    { dataKey: 'blocked', label: 'Bloqueados', color: '#F44336' },
];

interface FunnelStep { label: string; value: number; color: string; }

function FunnelChart({ steps, height = 140 }: { steps: FunnelStep[]; height?: number }) {
    const W = 600;
    const PAD_TOP = 22;
    const PAD_BOT = 34;
    const n = steps.length;
    const sw = W / n;
    const max = steps[0]?.value || 1;
    const cy = PAD_TOP + height / 2;
    const getH = (v: number) => Math.max((v / max) * height, 6);

    // Cada segmento es un trapezoid entre step[i] y step[i+1]
    const segments = steps.map((s, i) => {
        const x1 = i * sw;
        const lH = getH(s.value);
        const rH = i < n - 1 ? getH(steps[i + 1].value) : lH;
        const pts = [
            `${x1},${cy - lH / 2}`,
            `${x1 + sw},${cy - rH / 2}`,
            `${x1 + sw},${cy + rH / 2}`,
            `${x1},${cy + lH / 2}`,
        ].join(' ');
        const pct = i === 0 ? 100 : Math.round(s.value / max * 1000) / 10;
        const cx = x1 + sw / 2;
        return { pts, pct, cx, lH, x1, s };
    });

    return (
        <Box sx={{ width: '100%', mt: 1 }}>
            <svg viewBox={`0 0 ${W} ${height + PAD_TOP + PAD_BOT}`} style={{ width: '100%', display: 'block' }}>
                {segments.map(({ pts, pct, cx, lH, x1, s }, i) => (
                    <g key={s.label}>
                        <polygon points={pts} fill={s.color} opacity={0.85} />
                        {/* label arriba */}
                        <text x={cx} y={PAD_TOP - 6} textAnchor="middle" fontSize={11} fill="#555">{s.label}</text>
                        {/* porcentaje */}
                        <text x={cx} y={PAD_TOP + height + 16} textAnchor="middle" fontSize={12} fontWeight="bold" fill={s.color}>{pct}%</text>
                        {/* valor */}
                        <text x={cx} y={PAD_TOP + height + 29} textAnchor="middle" fontSize={10} fill="#999">{s.value.toLocaleString()}</text>
                    </g>
                ))}
            </svg>
        </Box>
    );
}

// Señales que Meta emite sobre CÓMO estamos enviando (comportamiento/calidad ante el usuario/ecosistema).
const META_SIGNAL_REASONS = [
    {
        key: 'frequency_capped',
        label: 'Freq. Cap',
        color: '#9C27B0',
        // Meta frenó el envío porque el usuario ya recibió demasiados templates ese día.
        // Problema si aparece frecuentemente en el mismo template — indica que estamos sobre-enviando al mismo segmento.
        tip: 'Frequency Capped: Meta limitó el envío por saturación al usuario. Si supera el 5% del total enviado, reducir frecuencia de campañas al mismo segmento.',
    },
    {
        key: 'quality_flagged',
        label: 'Calidad',
        color: '#E91E63',
        // Meta detectó que usuarios ignoran o reportan este template como spam.
        // Cualquier número es señal de alerta — si crece, Meta puede pausar el template automáticamente.
        tip: 'Quality Flagged: Meta marcó el template como baja calidad. Cualquier valor >0 es señal de alerta. Revisar contenido y relevancia del mensaje.',
    },
    {
        key: 'template_paused',
        label: 'T. Pausado',
        color: '#795548',
        // Meta pausó el template por calidad baja. Ya no se envía hasta que se reactive desde Meta Business Manager.
        // Acción urgente: entrar a Meta Business y revisar el template. El envío está detenido.
        tip: 'Template Pausado: Meta pausó este template. Los envíos están detenidos. Revisar en Meta Business Manager y corregir el contenido.',
    },
    {
        key: 'template_disabled',
        label: 'T. Desact.',
        color: '#607D8B',
        // Meta desactivó el template completamente. No se puede reactivar — hay que crear uno nuevo.
        // Acción urgente: notificar al equipo, el template está muerto.
        tip: 'Template Desactivado: Meta desactivó el template permanentemente. No se puede reactivar. Crear un template nuevo.',
    },
];

// Errores de CONFIGURACIÓN/DATOS nuestros — Meta ni siquiera evalúa el envío, lo rechaza antes.
// No cuentan como bloqueo del usuario ni afectan la salud/calidad del template ante Meta.
const CONFIG_ERROR_REASONS = [
    {
        key: 'account_not_registered',
        label: 'Núm. inválido',
        color: '#FF5722',
        tip: 'Account Not Registered: el número no existe o no está registrado en WhatsApp. Dato sucio en nuestra base — no es rechazo de Meta ni del usuario.',
    },
    {
        key: 'template_config_error',
        label: 'Template mal config.',
        color: '#FF5722',
        tip: 'Template Config Error: el template no existe o no está aprobado en ese idioma. Error de configuración nuestro — revisar el nombre/idioma configurado.',
    },
    {
        key: 'media_error',
        label: 'Media',
        color: '#FF5722',
        tip: 'Media Error: falló la descarga del media (imagen/video) del template. Normalmente transitorio (link caído o servidor lento).',
    },
    {
        key: 'payment_issue',
        label: 'Pago pendiente',
        color: '#FF5722',
        tip: 'Business Eligibility Payment Issue (131042): el método de pago de esta cuenta de WhatsApp Business no está configurado o fue rechazado por Meta. Revisar y actualizar el método de pago en Meta Business Manager.',
    },
    {
        key: 'send_rejected',
        label: 'Desconocido',
        color: '#9E9E9E',
        tip: 'Código de error de Meta aún no clasificado. Revisar el detalle (hover) para decidir si merece su propia categoría.',
    },
];

// Ambiguos: Meta agrupa varias causas distintas bajo el mismo código a propósito, sin revelar cuál aplicó
// (por privacidad). No podemos afirmar que sea culpa nuestra ni que sea bloqueo confirmado del usuario.
const AMBIGUOUS_REASONS = [
    {
        key: 'undeliverable',
        label: 'No entregable',
        color: '#FFC107',
        tip: 'Message Undeliverable (131026): Meta agrupa aquí varias causas sin especificar cuál — puede ser número no registrado, bloqueo/rechazo por calidad, o frequency capping. No se puede clasificar como error nuestro ni como bloqueo confirmado con la info disponible. Vigilar si crece.',
    },
];

function FailureChips({ row, reasons }: { row: Record<string, any>; reasons: typeof META_SIGNAL_REASONS }) {
    const active = reasons.filter(r => (row[r.key] ?? 0) > 0);
    if (!active.length) return null;
    return (
        <Box display="flex" flexWrap="wrap" gap={0.5}>
            {active.map(r => (
                <Tooltip
                    key={r.key}
                    title={<Box><Typography variant="caption" display="block" mb={0.5}>{r.tip}</Typography><ErrorCodeTooltip row={row} category={r.key} /></Box>}
                    arrow
                    placement="top"
                >
                    <Box sx={{ bgcolor: `${r.color}18`, border: `1px solid ${r.color}`, borderRadius: 1, px: 1, py: 0.25, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
                        <Typography variant="caption" style={{ color: r.color, fontWeight: 600 }}>{row[r.key]}</Typography>
                        <Typography variant="caption" color="textSecondary">{r.label}</Typography>
                    </Box>
                </Tooltip>
            ))}
        </Box>
    );
}

function FailureBreakdown({ row }: { row: Record<string, any> }) {
    const hasMetaSignals = META_SIGNAL_REASONS.some(r => (row[r.key] ?? 0) > 0);
    const hasConfigErrors = CONFIG_ERROR_REASONS.some(r => (row[r.key] ?? 0) > 0);
    const hasAmbiguous = AMBIGUOUS_REASONS.some(r => (row[r.key] ?? 0) > 0);
    if (!hasMetaSignals && !hasConfigErrors && !hasAmbiguous) return null;
    return (
        <Box mt={1}>
            {hasMetaSignals && (
                <Box mb={0.5}>
                    <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>Señales de Meta (afectan salud del template)</Typography>
                    <FailureChips row={row} reasons={META_SIGNAL_REASONS} />
                </Box>
            )}
            {hasConfigErrors && (
                <Box mb={0.5}>
                    <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>Errores de configuración nuestra (no cuentan como bloqueo)</Typography>
                    <FailureChips row={row} reasons={CONFIG_ERROR_REASONS} />
                </Box>
            )}
            {hasAmbiguous && (
                <Box>
                    <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>Sin clasificar (Meta no especifica la causa)</Typography>
                    <FailureChips row={row} reasons={AMBIGUOUS_REASONS} />
                </Box>
            )}
        </Box>
    );
}

export default function TemplateDashboard() {
    const [tab, setTab] = useState(0);
    const [byTemplate, setByTemplate] = useState<TemplateStats[]>([]);
    const [byProgram, setByProgram] = useState<ProgramStats[]>([]);
    const [blocks, setBlocks] = useState<BlockRecord[]>([]);
    const [groupNames, setGroupNames] = useState<Record<number, string>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
    const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);

    const localDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return localDate(d); };
    const today = localDate(new Date());
    const [dateFrom, setDateFrom] = useState(() => daysAgo(7));
    const [dateTo, setDateTo] = useState(() => today);

    const QUICK_RANGES = [
        { label: '7d', days: 7 },
        { label: '15d', days: 15 },
        { label: '1m', days: 30 },
        { label: '3m', days: 90 },
    ];
    const applyRange = (days: number) => { setDateFrom(daysAgo(days)); setDateTo(today); };


    useEffect(() => {
        setLoading(true);
        setError(null);
        const params = {
            ...(dateFrom && { date_from: dateFrom }),
            ...(dateTo && { date_to: dateTo }),
        };
        Promise.all([
            getTemplateDashboard(params),
            getTemplateDashboardByProgram(params),
            getTemplateBlocks(),
        ]).then(([t, p, b]) => {
            const templateData: TemplateStats[] = t.data;
            const programData: ProgramStats[] = p.data;
            setByTemplate(templateData);
            setByProgram(programData);
            setBlocks(b.data.results ?? b.data);
            setSelectedTemplates(defaultTemplates(templateData));
            setSelectedPrograms(defaultPrograms(programData));
            const uniqueIds = Array.from(new Set(programData.map((r: ProgramStats) => r.program_id)));
            Promise.all(
                uniqueIds.map((id: number) =>
                    ProgramsRequest.get(`/user_groups/get_program_name_by_group_id/${id}/`)
                        .then(r => ({ id, name: r.data?.program_name ?? `Grupo ${id}` }))
                        .catch(() => ({ id, name: `Grupo ${id}` }))
                )
            ).then(results => {
                const map: Record<number, string> = {};
                results.forEach(({ id, name }) => { map[id] = name; });
                setGroupNames(map);
            });
        }).catch((err) => {
            setError(`Error al cargar datos: ${err?.response?.status ?? err.message}`);
        }).finally(() => setLoading(false));
    }, [dateFrom, dateTo]);

    const programName = (id: number) => groupNames[id] ?? `Grupo ${id}`;
    const allProgramIds = Array.from(new Set(byProgram.map(r => r.program_id)));

    const allTemplateNames = byTemplate.map(r => r.template_name);
    const filteredByTemplate = byTemplate.filter(r => selectedTemplates.includes(r.template_name));

    type ProgramAgg = { program_label: string; sent: number; delivered: number; read: number; blocked: number; frequency_capped: number; quality_flagged: number; template_paused: number; template_disabled: number; undeliverable: number; delivery_rate: number; read_rate: number; quality_rate: number; frequency_rate: number; };
    const byProgramAgg = Object.values(
        byProgram
            .filter(r => selectedTemplates.includes(r.template_name) && selectedPrograms.includes(r.program_id))
            .reduce<Record<string, ProgramAgg>>(
                (acc, r) => {
                    const key = String(r.program_id);
                    if (!acc[key]) acc[key] = { program_label: programName(r.program_id), sent: 0, delivered: 0, read: 0, blocked: 0, frequency_capped: 0, quality_flagged: 0, template_paused: 0, template_disabled: 0, undeliverable: 0, delivery_rate: 0, read_rate: 0, quality_rate: 0, frequency_rate: 0 };
                    acc[key].sent += r.sent;
                    acc[key].delivered += r.delivered;
                    acc[key].read += r.read;
                    acc[key].blocked += r.blocked;
                    acc[key].frequency_capped += r.frequency_capped ?? 0;
                    acc[key].quality_flagged += r.quality_flagged ?? 0;
                    acc[key].template_paused += r.template_paused ?? 0;
                    acc[key].template_disabled += r.template_disabled ?? 0;
                    acc[key].undeliverable += r.undeliverable ?? 0;
                    return acc;
                }, {}
            )
    ).map(row => {
        const sent = row.sent || 0;
        // Calidad = bloqueados + quality_flagged + undeliverable. "undeliverable" (131026) es un bucket
        // ambiguo de Meta que puede incluir bloqueos silenciosos — se suma al riesgo aunque no se pueda
        // confirmar caso por caso (ver AMBIGUOUS_REASONS).
        return {
            ...row,
            delivery_rate: sent ? Math.round(row.delivered / sent * 1000) / 10 : 0,
            read_rate: sent ? Math.round(row.read / sent * 1000) / 10 : 0,
            quality_rate: sent ? Math.round((row.blocked + row.quality_flagged + row.undeliverable) / sent * 10000) / 100 : 0,
            frequency_rate: sent ? Math.round(row.frequency_capped / sent * 10000) / 100 : 0,
        };
    });

    const totals = byTemplate.reduce(
        (acc, r) => ({ sent: acc.sent + r.sent, delivered: acc.delivered + r.delivered, read: acc.read + r.read, blocked: acc.blocked + r.blocked }),
        { sent: 0, delivered: 0, read: 0, blocked: 0 }
    );

    return (
        <Grid container spacing={3} style={{ padding: 24 }}>
            <Grid item xs={12}>
                <Typography variant="h5">Template Dashboard</Typography>
            </Grid>

            {/* Fechas + pills agrupados a la izquierda */}
            <Grid item xs={12} md={6}>
                <Box display="flex" gap={1} flexWrap="wrap">
                    <Box flex={1} minWidth={130}>
                        <TextField label="Desde" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }}
                            value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                    </Box>
                    <Box flex={1} minWidth={130}>
                        <TextField label="Hasta" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }}
                            value={dateTo} onChange={e => setDateTo(e.target.value)} />
                    </Box>
                </Box>
                <Box display="flex" gap={0.5} mt={1}>
                    {QUICK_RANGES.map(r => (
                        <Chip
                            key={r.label}
                            label={r.label}
                            size="small"
                            onClick={() => applyRange(r.days)}
                            variant={dateFrom === daysAgo(r.days) && dateTo === today ? 'filled' : 'outlined'}
                            color={dateFrom === daysAgo(r.days) && dateTo === today ? 'primary' : 'default'}
                            sx={{ cursor: 'pointer' }}
                        />
                    ))}
                </Box>
            </Grid>

            {error && (
                <Grid item xs={12}><Alert severity="error">{error}</Alert></Grid>
            )}
            {loading ? (
                <Grid item xs={12}><Box display="flex" justifyContent="center"><CircularProgress /></Box></Grid>
            ) : (
                <>
                    <Grid item xs={6} sm={3}><StatCard label="Enviados" value={totals.sent} color="#2196F3" /></Grid>
                    <Grid item xs={6} sm={3}><StatCard label="Entregados" value={totals.delivered} color="#4CAF50" /></Grid>
                    <Grid item xs={6} sm={3}><StatCard label="Leídos" value={totals.read} color="#FF9800" /></Grid>
                    <Grid item xs={6} sm={3}><StatCard label="Bloqueados" value={totals.blocked} color="#F44336" /></Grid>

                    <Grid item xs={12}>
                        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                            <Tab label="Por Template" />
                            <Tab label="Por Programa" />
                            <Tab label="Bloqueos" />
                        </Tabs>
                    </Grid>

                    {tab === 0 && (
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Templates</InputLabel>
                                <Select
                                    multiple
                                    value={selectedTemplates}
                                    onChange={e => setSelectedTemplates(typeof e.target.value === 'string' ? [e.target.value] : e.target.value as string[])}
                                    input={<OutlinedInput label="Templates" />}
                                    renderValue={(selected) => (<ChipsRenderValue values={selected as string[]} getLabel={(v: string) => v} />)}
                                >
                                    {allTemplateNames.map(name => (
                                        <MenuItem key={name} value={name}>{name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    {tab === 0 && filteredByTemplate.map(row => (
                        <Grid item xs={12} sm={6} md={4} key={row.template_name}>
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle2" noWrap title={row.template_name} gutterBottom>
                                        {row.template_name}
                                    </Typography>
                                    <Semaforo delivery_rate={row.delivery_rate} read_rate={row.read_rate} quality_rate={row.sent ? Math.round((row.blocked + (row.quality_flagged ?? 0) + (row.undeliverable ?? 0)) / row.sent * 10000) / 100 : 0} frequency_rate={row.sent ? Math.round((row.frequency_capped ?? 0) / row.sent * 10000) / 100 : 0} />
                                    <FunnelChart steps={[
                                        { label: 'Enviados', value: row.sent, color: '#2196F3' },
                                        { label: 'Entregados', value: row.delivered, color: '#4CAF50' },
                                        { label: 'Leídos', value: row.read, color: '#FF9800' },
                                        { label: 'Bloqueados', value: row.blocked, color: '#F44336' },
                                    ]} />
                                    <Box display="flex" justifyContent="space-around" mt={0.5}>
                                        {STATS_ROW.map(s => {
                                            const box = (
                                                <Box key={s.dataKey} textAlign="center" sx={s.dataKey === 'blocked' ? { cursor: 'default' } : undefined}>
                                                    <Typography variant="caption" style={{ color: s.color }}>{s.label}</Typography>
                                                    <Typography variant="body2"><strong>{(row as any)[s.dataKey]}</strong></Typography>
                                                </Box>
                                            );
                                            if (s.dataKey !== 'blocked') return box;
                                            return (
                                                <Tooltip key={s.dataKey} title={<ErrorCodeTooltip row={row} category="blocked" />} arrow placement="top">
                                                    {box}
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                    <FailureBreakdown row={row} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}

                    {tab === 1 && (
                        <Grid item xs={12}>
                            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="flex-end" mb={2}>
                                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 300 } }}>
                                    <InputLabel>Programas</InputLabel>
                                    <Select
                                        multiple
                                        value={selectedPrograms}
                                        onChange={e => setSelectedPrograms(typeof e.target.value === 'string' ? [] : e.target.value as number[])}
                                        input={<OutlinedInput label="Programas" />}
                                        renderValue={(selected) => (<ChipsRenderValue values={selected as number[]} getLabel={(v: number) => programName(v)} />)}
                                    >
                                        {allProgramIds.map(id => (
                                            <MenuItem key={id} value={id}>{programName(id)}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Grid>
                    )}
                    {tab === 1 && byProgramAgg.map(row => (
                        <Grid item xs={12} sm={6} md={4} key={row.program_label}>
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle2" noWrap title={row.program_label} gutterBottom>
                                        {row.program_label}
                                    </Typography>
                                    <Semaforo delivery_rate={row.delivery_rate} read_rate={row.read_rate} quality_rate={row.quality_rate} frequency_rate={row.frequency_rate} />
                                    <FunnelChart steps={[
                                        { label: 'Enviados', value: row.sent, color: '#2196F3' },
                                        { label: 'Entregados', value: row.delivered, color: '#4CAF50' },
                                        { label: 'Leídos', value: row.read, color: '#FF9800' },
                                        { label: 'Bloqueados', value: row.blocked, color: '#F44336' },
                                    ]} />
                                    <Box display="flex" justifyContent="space-around" mt={0.5}>
                                        {STATS_ROW.map(s => {
                                            const box = (
                                                <Box key={s.dataKey} textAlign="center" sx={s.dataKey === 'blocked' ? { cursor: 'default' } : undefined}>
                                                    <Typography variant="caption" style={{ color: s.color }}>{s.label}</Typography>
                                                    <Typography variant="body2"><strong>{(row as any)[s.dataKey]}</strong></Typography>
                                                </Box>
                                            );
                                            if (s.dataKey !== 'blocked') return box;
                                            return (
                                                <Tooltip key={s.dataKey} title={<ErrorCodeTooltip row={row} category="blocked" />} arrow placement="top">
                                                    {box}
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                    <FailureBreakdown row={row} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}

                    {tab === 2 && (
                        <Grid item xs={12} container spacing={3}>
                            <Grid item xs={12} sm={5}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="subtitle1" gutterBottom>Bloqueos por template</Typography>
                                        {(() => {
                                            const PIE_COLORS = ['#009688','#3F51B5','#9C27B0','#673AB7','#2196F3','#00BCD4','#4CAF50','#FF9800','#F44336','#E91E63'];
                                            const pieData = filteredByTemplate.filter(r => r.blocked > 0).map((r, i) => ({ id: i, value: r.blocked, label: r.template_name, color: PIE_COLORS[i % PIE_COLORS.length] }));
                                            return (
                                                <>
                                                    <PieChart
                                                        height={260}
                                                        series={[{ data: pieData }]}
                                                        legend={{ hidden: true }}
                                                        margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
                                                    />
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                                        {pieData.map(d => (
                                                            <Box key={d.id} display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                                                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: d.color, flexShrink: 0 }} />
                                                                <Typography variant="caption" noWrap title={d.label}>{d.label} ({d.value})</Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </>
                                            );
                                        })()}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="subtitle1" gutterBottom>Últimos bloqueados ({blocks.length})</Typography>
                                        {blocks.slice(0, 10).map(b => (
                                            <Box key={b.id} display="flex" justifyContent="space-between" py={0.5} borderBottom="1px solid #eee">
                                                <Typography variant="body2">{b.user_channel}</Typography>
                                                <Typography variant="body2" color="textSecondary">{b.template_name}</Typography>
                                                <Typography variant="body2" color="textSecondary">{new Date(b.created_at).toLocaleDateString()}</Typography>
                                            </Box>
                                        ))}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </>
            )}
        </Grid>
    );
}
